import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, IconButton } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const ImageSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      image: '/slides/pexels-yankrukov-5794039.jpg',
      title: 'Professional Chiropractic Care',
      subtitle: 'Expert spinal adjustments and therapeutic treatments in Melton',
    },
    {
      image: '/slides/pexels-ryutaro-5473215.jpg',
      title: 'Therapeutic Massage',
      subtitle: 'Professional remedial massage therapy for muscle tension relief',
    },
    {
      image: '/slides/pexels-gustavo-fring-5888095.jpg',
      title: 'Back Pain Relief',
      subtitle: 'Targeted chiropractic care for chronic and acute back pain',
    },
    {
      image: '/slides/pexels-yankrukov-5794024.jpg',
      title: 'Sports Injury Treatment',
      subtitle: 'Specialized care for athletes and workplace injuries',
    },
    {
      image: '/slides/pexels-yankrukov-5794033.jpg',
      title: 'Manual Therapy',
      subtitle: 'Hands-on treatment techniques for musculoskeletal conditions',
    },
    {
      image: '/slides/pexels-yankrukov-5794039.jpg',
      title: 'Wellness & Recovery',
      subtitle: 'Comprehensive care for optimal health and wellbeing',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // Slower transition for better stability
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <Box
      sx={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        height: { xs: '50vh', sm: '60vh', md: '70vh' },
        my: { xs: 6, md: 8 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          backgroundImage: `url(${currentSlideData.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-image 1s ease-in-out',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.4), rgba(44, 62, 80, 0.3))',
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4 } }}>
          <MotionBox
            key={currentSlide}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{
              textAlign: 'center',
              color: 'white',
              px: { xs: 2, md: 0 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: { xs: 1.5, md: 2 },
                fontWeight: 700,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                lineHeight: { xs: 1.2, md: 1.3 },
              }}
            >
              {currentSlideData.title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                maxWidth: '600px',
                mx: 'auto',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.5rem' },
                lineHeight: { xs: 1.4, md: 1.5 },
                px: { xs: 1, md: 0 },
              }}
            >
              {currentSlideData.subtitle}
            </Typography>
          </MotionBox>
        </Container>

        {/* Navigation Arrows */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: 'absolute',
            left: { xs: 8, md: 20 },
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(44, 62, 80, 0.8)',
            color: 'white',
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            '&:hover': {
              background: 'rgba(44, 62, 80, 1)',
              transform: { xs: 'translateY(-50%)', md: 'translateY(-50%) scale(1.1)' },
            },
            transition: 'all 0.3s ease',
            '& svg': { fontSize: { xs: 18, md: 24 } },
          }}
        >
          <ArrowBackIos />
        </IconButton>

        <IconButton
          onClick={nextSlide}
          sx={{
            position: 'absolute',
            right: { xs: 8, md: 20 },
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(44, 62, 80, 0.8)',
            color: 'white',
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            '&:hover': {
              background: 'rgba(44, 62, 80, 1)',
              transform: { xs: 'translateY(-50%)', md: 'translateY(-50%) scale(1.1)' },
            },
            transition: 'all 0.3s ease',
            '& svg': { fontSize: { xs: 18, md: 24 } },
          }}
        >
          <ArrowForwardIos />
        </IconButton>

        {/* Dots Indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 20, md: 30 },
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: { xs: 0.5, md: 1 },
          }}
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentSlide(index)}
              sx={{
                width: { xs: 8, md: 12 },
                height: { xs: 8, md: 12 },
                borderRadius: '50%',
                background: index === currentSlide ? '#2c3e50' : 'white',
                opacity: index === currentSlide ? 1 : 0.7,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  opacity: 1,
                  transform: { xs: 'scale(1.1)', md: 'scale(1.2)' },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ImageSlider;